import { JSX } from "react";
import {
  Column_ColumnPinning,
  Column_ColumnSizing,
  RowData,
  Table,
  TableFeatures,
  Table_ColumnVisibility,
} from "@tanstack/react-table";
import {
  ColumnGroupBoundaries,
  EMPTY_COLUMN_GROUP_BOUNDARIES,
} from "@app/shared/virtualized-table/getColumnGroupBoundaries";

interface VirtualizedTableRowSkeletonProps<TFeatures extends TableFeatures, TData extends RowData> {
  /** See getColumnGroupBoundaries — draws a vertical divider marking each column group's edges. */
  columnGroupBoundaries?: ColumnGroupBoundaries;
  rowHeight: number;
  table: Table<TFeatures, TData>;
}

/**
 * One placeholder row shaped like a real VirtualizedTableRow — reads its columns straight from the
 * already-constructed table instance (column defs don't depend on data), so it shows the actual
 * frozen columns and column widths instead of a generic/differently-shaped placeholder. `rowHeight`
 * must match the real row height the consumer's virtualizer uses, passed explicitly rather than
 * hardcoded in CSS so the two can't drift apart.
 *
 * The `as typeof x & Feature_Xyz` casts below are load-bearing — see VirtualizedTableHeader's
 * doc comment for why an abstract `TFeatures` type parameter can't resolve feature-contributed
 * methods (`getVisibleLeafColumns`, `getIsPinned`, `getStart`, `getSize`) on its own.
 */
export const VirtualizedTableRowSkeleton = <TFeatures extends TableFeatures, TData extends RowData>({
  columnGroupBoundaries = EMPTY_COLUMN_GROUP_BOUNDARIES,
  rowHeight,
  table,
}: VirtualizedTableRowSkeletonProps<TFeatures, TData>): JSX.Element => (
  <div className="virtualized-table__skeleton-row skeleton shimmer" role="row" style={{ height: rowHeight }}>
    {(table as typeof table & Table_ColumnVisibility<TFeatures, TData>).getVisibleLeafColumns().map((column) => {
      const pinnedColumn = column as typeof column & Column_ColumnPinning & Column_ColumnSizing;
      const isPinned = pinnedColumn.getIsPinned() === "start";
      const isGroupStart = columnGroupBoundaries.groupStartIds.has(column.id);
      const isGroupEnd = columnGroupBoundaries.groupEndIds.has(column.id);
      return (
        <div
          key={column.id}
          className={`virtualized-table__cell${isPinned ? " virtualized-table__cell--pinned" : ""}${
            isGroupStart ? " virtualized-table__cell--group-start" : ""
          }${isGroupEnd ? " virtualized-table__cell--group-end" : ""}`}
          style={{
            flexGrow: isPinned ? 0 : 1,
            left: isPinned ? pinnedColumn.getStart("start") : undefined,
            width: pinnedColumn.getSize(),
          }}
          role="cell"
        >
          <div className="bone virtualized-table__skeleton-bone" />
        </div>
      );
    })}
  </div>
);
